/**
 * i18n (국제화) 시스템
 *
 * 영어/한국어 다국어 지원을 위한 번역 시스템입니다.
 * React Context API를 사용하여 앱 전체에서 언어 설정을 공유합니다.
 *
 * 사용법:
 * 1. App.tsx에서 I18nProvider로 감싸기
 * 2. 컴포넌트에서 useI18n() 훅 사용
 * 3. t('key') 함수로 번역된 텍스트 가져오기
 */

import { createContext, useContext, useState, ReactNode } from 'react'

// =============================================================================
// 타입 정의
// =============================================================================

/** 지원하는 언어 코드 */
export type Language = 'ko' | 'en'

/** 번역 키 타입 - 모든 번역 키를 타입으로 정의하여 오타 방지 */
export type TranslationKey = keyof typeof translations.ko

/** i18n 컨텍스트 타입 */
interface I18nContextType {
  /** 현재 언어 */
  language: Language
  /** 언어 변경 함수 */
  setLanguage: (lang: Language) => void
  /** 번역 함수 - 키를 넣으면 현재 언어에 맞는 텍스트 반환 */
  t: (key: TranslationKey) => string
}

// =============================================================================
// 번역 데이터
// =============================================================================

/**
 * 번역 데이터 객체
 *
 * 구조: { 언어코드: { 키: 번역문자열 } }
 *
 * 새 텍스트 추가 시:
 * 1. ko에 한국어 추가
 * 2. en에 영어 추가
 * 3. 둘 다 같은 키 사용해야 함
 */
export const translations = {
  // ---------------------------------------------------------------------------
  // 한국어 번역
  // ---------------------------------------------------------------------------
  ko: {
    // 공통
    appName: 'SearchYourMelody',
    loading: '로딩 중...',
    error: '오류가 발생했습니다',
    retry: '다시 시도',
    search: '검색',

    // 네비게이션
    navHome: '홈',
    navHumming: '허밍 검색',
    navLyrics: '가사 검색',

    // 홈페이지 - 히어로 섹션
    heroTitle: '노래가 생각나지 않을 때',
    heroSubtitle: '들리는대로 입력하거나 노래를 불러 찾아보세요',

    // 탭
    tabHumming: '허밍으로 찾기',
    tabLyrics: '가사로 찾기',

    // 허밍 검색
    hummingTitle: '허밍으로 곡 찾기',
    hummingDescription: '기억나는 멜로디를 허밍하면 어떤 곡인지 찾아드려요',
    hummingTip: '조용한 환경에서 멜로디를 또렷하게 녹음해주세요',
    hummingDuration: '7-10초 정도가 적당해요',
    hummingLimitation: '※ 음정 변화가 적은 곡은 검색이 어려울 수 있어요',
    pressToRecord: '버튼을 눌러 멜로디를 녹음하세요',
    requestingMic: '마이크 권한을 요청 중...',
    processing: '처리 중...',
    minDurationWarning: '최소 {seconds}초 이상 녹음해주세요',
    pressToStop: '버튼을 눌러 녹음을 완료하세요',
    uploading: '업로드 중...',
    uploadingDesc: '허밍을 서버로 전송하고 있어요',
    searching: '검색 중...',
    searchingDesc: '데이터베이스에서 비슷한 곡을 찾고 있어요',

    // 가사 검색
    lyricsTitle: '가사로 곡 찾기',
    lyricsDescription: '기억나는 가사 일부를 입력하세요',
    lyricsPlaceholder: '예: 내 맘이 보이지, kiss me more...',
    lyricsTip: '원곡 언어로 검색하면 더 정확해요 (한글 가사는 한글로, 영어 가사는 영어로)',

    // 검색 결과
    resultsFound: '{count}개의 비슷한 곡을 찾았어요!',
    noResults: '비슷한 곡을 찾지 못했어요',
    noResultsHint: '다시 한 번 더 또렷하게 허밍해보시거나, 다른 부분의 멜로디를 시도해보세요.',
    processingTime: '처리 시간: {ms}ms',
    matchedSegment: '매칭 구간',
    finalScore: '종합 점수',
    similarityVeryHigh: '매우 높음',
    similarityHigh: '높음',
    similarityMedium: '보통',
    similarityLow: '낮음',
    listenOnDeezer: 'Deezer에서 듣기',

    // 레코더
    micPermissionDenied: '마이크 권한이 거부되었습니다. 브라우저 설정에서 허용해주세요.',
    micNotFound: '마이크를 찾을 수 없습니다. 마이크가 연결되어 있는지 확인해주세요.',
    micError: '마이크 오류',
    recordingFailed: '녹음을 시작할 수 없습니다.',

    // 팁
    tipsTitle: '더 정확한 결과를 위한 팁',
    tip1: '기억나는 가장 특징적인 부분을 불러보세요',
    tip2: '조용한 환경에서 녹음하세요',
    tip3: '7-10초 정도 녹음하면 가장 좋아요',
    tip4: '음 높낮이가 뚜렷한 노래가 가장 정확하게 검색돼요',

    // 허밍 안내 (상단 강조)
    hummingGuide: '가장 편한 음역대에서, 또렷하게 불러주세요',

    // Rate Limiting
    rateLimitWarning: '너무 많은 요청을 보냈습니다. {seconds}초 후에 다시 시도해주세요.',

    // 푸터
    footerTech: 'FastAPI + React + PostgreSQL + ChromaDB',
    footerData: 'Data from Deezer API',
  },

  // ---------------------------------------------------------------------------
  // 영어 번역
  // ---------------------------------------------------------------------------
  en: {
    // Common
    appName: 'SearchYourMelody',
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    search: 'Search',

    // Navigation
    navHome: 'Home',
    navHumming: 'Humming',
    navLyrics: 'Lyrics',

    // Homepage - Hero section
    heroTitle: "Can't remember that song?",
    heroSubtitle: 'Type what you hear or hum to find your song',

    // Tabs
    tabHumming: 'Search by Humming',
    tabLyrics: 'Search by Lyrics',

    // Humming search
    hummingTitle: 'Find Song by Humming',
    hummingDescription: "Hum the melody you remember and we'll find the song",
    hummingTip: 'Record clearly in a quiet environment',
    hummingDuration: '7-10 seconds works best',
    hummingLimitation: '※ songs with minimal pitch variation may not be searchable',
    pressToRecord: 'Press the button to record the melody',
    requestingMic: 'Requesting microphone access...',
    processing: 'Processing...',
    minDurationWarning: 'Please record at least {seconds} seconds',
    pressToStop: 'Press to stop recording',
    uploading: 'Uploading...',
    uploadingDesc: 'Sending your humming to the server',
    searching: 'Searching...',
    searchingDesc: 'Looking for similar songs in the database',

    // Lyrics search
    lyricsTitle: 'Find Song by Lyrics',
    lyricsDescription: 'Enter some lyrics you remember',
    lyricsPlaceholder: 'e.g., love me like, shape of you...',
    lyricsTip: 'Search in the original language for better accuracy (Korean lyrics in Korean, English lyrics in English)',

    // Search results
    resultsFound: 'Found {count} similar songs!',
    noResults: "Couldn't find any similar songs",
    noResultsHint: 'Try humming more clearly, or try a different part of the melody.',
    processingTime: 'Processing time: {ms}ms',
    matchedSegment: 'Matched segment',
    finalScore: 'Score',
    similarityVeryHigh: 'Very High',
    similarityHigh: 'High',
    similarityMedium: 'Medium',
    similarityLow: 'Low',
    listenOnDeezer: 'Listen on Deezer',

    // Recorder
    micPermissionDenied: 'Microphone access denied. Please allow it in browser settings.',
    micNotFound: 'No microphone found. Please check if a microphone is connected.',
    micError: 'Microphone error',
    recordingFailed: 'Failed to start recording.',

    // Tips
    tipsTitle: 'Tips for better results',
    tip1: 'Try the most distinctive part you remember',
    tip2: 'Record in a quiet environment',
    tip3: '7-10 seconds recording works best',
    tip4: 'Songs with clear pitch variations work best',

    // 허밍 안내 (상단 강조)
    hummingGuide: 'Sing in your comfortable range with clear pitch',

    // Rate Limiting
    rateLimitWarning: 'Too many requests. Please try again in {seconds} seconds.',

    // Footer
    footerTech: 'FastAPI + React + PostgreSQL + ChromaDB',
    footerData: 'Data from Deezer API',
  }
} as const

// =============================================================================
// Context 생성
// =============================================================================

/** i18n Context - 앱 전체에서 언어 설정 공유 */
const I18nContext = createContext<I18nContextType | null>(null)

// =============================================================================
// Provider 컴포넌트
// =============================================================================

interface I18nProviderProps {
  children: ReactNode
}

/**
 * I18nProvider
 *
 * 앱 최상단에서 감싸서 사용합니다.
 * localStorage에 언어 설정을 저장하여 새로고침해도 유지됩니다.
 *
 * @example
 * ```tsx
 * // main.tsx
 * <I18nProvider>
 *   <App />
 * </I18nProvider>
 * ```
 */
export function I18nProvider({ children }: I18nProviderProps) {
  // localStorage에서 저장된 언어 불러오기, 없으면 브라우저 언어 감지
  const [language, setLanguageState] = useState<Language>(() => {
    // 1. localStorage 확인
    const saved = localStorage.getItem('language') as Language
    if (saved && (saved === 'ko' || saved === 'en')) {
      return saved
    }

    // 2. 브라우저 언어 감지
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith('ko')) {
      return 'ko'
    }

    // 3. 기본값: 영어
    return 'en'
  })

  /**
   * 언어 변경 함수
   * localStorage에도 저장하여 새로고침 시에도 유지
   */
  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem('language', lang)
  }

  /**
   * 번역 함수
   *
   * @param key - 번역 키
   * @returns 현재 언어에 맞는 번역된 문자열
   *
   * @example
   * ```tsx
   * const { t } = useI18n()
   * return <h1>{t('heroTitle')}</h1>
   * ```
   */
  const t = (key: TranslationKey): string => {
    return translations[language][key] || key
  }

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

/**
 * useI18n Hook
 *
 * 컴포넌트에서 다국어 기능을 사용하기 위한 훅입니다.
 *
 * @returns { language, setLanguage, t }
 * - language: 현재 언어 ('ko' | 'en')
 * - setLanguage: 언어 변경 함수
 * - t: 번역 함수
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { language, setLanguage, t } = useI18n()
 *
 *   return (
 *     <div>
 *       <h1>{t('heroTitle')}</h1>
 *       <button onClick={() => setLanguage('en')}>English</button>
 *       <button onClick={() => setLanguage('ko')}>한국어</button>
 *     </div>
 *   )
 * }
 * ```
 */
export function useI18n(): I18nContextType {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider')
  }

  return context
}

// =============================================================================
// 유틸리티 함수
// =============================================================================

/**
 * 템플릿 문자열 치환 함수
 *
 * 번역 문자열에 동적 값을 넣을 때 사용합니다.
 * {key} 형태의 플레이스홀더를 실제 값으로 치환합니다.
 *
 * @param template - 번역된 문자열 (플레이스홀더 포함)
 * @param values - 치환할 값들의 객체
 * @returns 치환된 문자열
 *
 * @example
 * ```tsx
 * const { t } = useI18n()
 * // t('resultsFound') = '{count}개의 비슷한 곡을 찾았어요!'
 * const message = interpolate(t('resultsFound'), { count: 5 })
 * // message = '5개의 비슷한 곡을 찾았어요!'
 * ```
 */
export function interpolate(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(
    /\{(\w+)\}/g,
    (_, key) => String(values[key] ?? `{${key}}`)
  )
}
